import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, View, Text, Alert, FormFieldGroup, SimpleSelect, RangeInput, Flex, IconButton, Tooltip, ScreenReaderContent } from '@smarterservices/smarter-ui';
import { IconPlayLine, IconStopLine } from '@instructure/ui-icons';
import { useMediaDeviceSelect, useMaybeRoomContext } from '@livekit/components-react';
import { getStore, useSettingsStore, useAppStore, usePlayerStore } from '../../../stores/StoreContext';
import CONSTANTS from '../../../config/constants';
/**
 * AudioSettings Component
 * 
 * Provides UI for selecting audio input (microphone) and output (speaker) devices.
 * Device selections are automatically applied via LiveKit and persisted to localStorage
 * for use across sessions. Uses label-based matching to handle device ID changes.
 * 
 * Features:
 * - Enumerates available audio devices using LiveKit
 * - Saves device preferences with both ID and label for reliable matching
 * - Automatically applies saved preferences on load
 * - Falls back to system defaults if saved device is unavailable
 * - No save button required - changes apply immediately
 * 
 * @returns {JSX.Element} Audio settings interface
 */
function AudioSettings() {
    const SettingsStore = getStore('settingsStore');
    const PlayerStore = getStore('playerStore');
    const AppStore = getStore('appStore');
    const room = useMaybeRoomContext();
    const audioInputDevice = useSettingsStore((state) => state.audioInputDevice);
    const audioOutputDevice = useSettingsStore((state) => state.audioOutputDevice);
    const enabledComponents = useAppStore((state) => state.enabledComponents);
    const attachedToProctorRoomSid = useAppStore((state) => state.attachedToProctorRoomSid);
    const volume = usePlayerStore((state) => state.volume);
    const [micInitialized, setMicInitialized] = useState(false);
    const [speakerInitialized, setSpeakerInitialized] = useState(false);
    const [isPlayingTestSound, setIsPlayingTestSound] = useState(false);
    const autoStopTimerRef = useRef(null);
    const volumeUpdateTimerRef = useRef(null);
    const SOUND_TEST_DURATION = 5000;

    // Check if microphone component is enabled
    const isMicrophoneEnabled = enabledComponents?.microphone !== false;

    // Throttled volume update function
    const throttledVolumeUpdate = useCallback((volume) => {
        if (volumeUpdateTimerRef.current) {
            return; // Skip if already scheduled
        }

        volumeUpdateTimerRef.current = setTimeout(() => {
            if (isPlayingTestSound) {
                const newVolume = volume / 100;
                AppStore.setSoundVolume('audio-check', newVolume);
            }
            volumeUpdateTimerRef.current = null;
        }, 50); // Update every 50ms max (20 times per second)
    }, [isPlayingTestSound, AppStore]);

    // Get microphone devices using LiveKit's device enumeration
    const {
        devices: microphoneDevices,
        activeDeviceId: activeMicrophoneId,
        setActiveMediaDevice: setActiveMicrophone,
    } = useMediaDeviceSelect({
        kind: 'audioinput',
        room,
        requestPermissions: true,
    });

    // Get speaker devices using LiveKit's device enumeration
    const {
        devices: speakerDevices,
        activeDeviceId: activeSpeakerId,
        setActiveMediaDevice: setActiveSpeaker,
    } = useMediaDeviceSelect({
        kind: 'audiooutput',
        room,
        requestPermissions: false,
    });

    /**
     * Initialize microphone device selection
     * 
     * Runs once when devices are loaded. Attempts to apply saved preference
     * using label-based matching (more reliable than ID-only). Falls back to
     * system default if saved device is unavailable.
     */
    useEffect(() => {
        if (microphoneDevices.length === 0 || micInitialized) return;

        // If we have a saved preference, try to match by label first, then by ID
        if (audioInputDevice) {
            // Try to find device by label (more reliable across sessions)
            let matchedDevice = microphoneDevices.find(d => d.label === audioInputDevice.label);

            // Fallback to ID match if label doesn't match
            if (!matchedDevice) {
                matchedDevice = microphoneDevices.find(d => d.deviceId === audioInputDevice.deviceId);
            }

            if (matchedDevice) {
                // Apply saved preference (even if it's the default)
                // Only call setActiveMicrophone if it's different from current
                if (activeMicrophoneId !== matchedDevice.deviceId) {
                    setActiveMicrophone(matchedDevice.deviceId).catch(err => {
                        console.error('[AudioSettings] Failed to apply saved microphone:', err);
                    });
                }
                setMicInitialized(true);
            } else {
                // Saved device no longer exists, save system default
                const defaultDevice = microphoneDevices[0];
                SettingsStore.setAudioInputDevice(defaultDevice.deviceId, defaultDevice.label, true);
                setMicInitialized(true);
            }
        } else if (microphoneDevices.length > 0) {
            // No saved preference, save system default
            const defaultDevice = activeMicrophoneId === 'default' ? microphoneDevices[0] :
                microphoneDevices.find(d => d.deviceId === activeMicrophoneId) || microphoneDevices[0];
            SettingsStore.setAudioInputDevice(defaultDevice.deviceId, defaultDevice.label, true);
            setMicInitialized(true);
        }
    }, [microphoneDevices, micInitialized]);

    // Initialize speaker - only run once when devices are loaded
    useEffect(() => {
        if (speakerDevices.length === 0 || speakerInitialized) return;

        // If we have a saved preference, try to match by label first, then by ID
        if (audioOutputDevice) {
            // Try to find device by label (more reliable across sessions)
            let matchedDevice = speakerDevices.find(d => d.label === audioOutputDevice.label);

            // Fallback to ID match if label doesn't match
            if (!matchedDevice) {
                matchedDevice = speakerDevices.find(d => d.deviceId === audioOutputDevice.deviceId);
            }

            if (matchedDevice) {
                // Apply saved preference (even if it's the default)
                // Only call setActiveSpeaker if it's different from current
                if (activeSpeakerId !== matchedDevice.deviceId) {
                    setActiveSpeaker(matchedDevice.deviceId).catch(err => {
                        console.error('[AudioSettings] Failed to apply saved speaker:', err);
                    });
                }
                setSpeakerInitialized(true);
            } else {
                // Saved device no longer exists, save system default
                const defaultDevice = speakerDevices[0];
                SettingsStore.setAudioOutputDevice(defaultDevice.deviceId, defaultDevice.label, true);
                setSpeakerInitialized(true);
            }
        } else if (speakerDevices.length > 0) {
            // No saved preference, save system default
            const defaultDevice = activeSpeakerId === 'default' ? speakerDevices[0] :
                speakerDevices.find(d => d.deviceId === activeSpeakerId) || speakerDevices[0];
            SettingsStore.setAudioOutputDevice(defaultDevice.deviceId, defaultDevice.label, true);
            setSpeakerInitialized(true);
        }
    }, [speakerDevices, speakerInitialized]);

    // Mark initial load complete after both devices are initialized
    useEffect(() => {
        if (micInitialized && speakerInitialized) {
            SettingsStore.markInitialLoadComplete();
        }
    }, [micInitialized, speakerInitialized]);

    /**
     * Handle microphone device selection change
     * 
     * Applies the selected device via LiveKit and saves the preference
     * (both deviceId and label) to localStorage for future sessions.
     * 
     * @param {Event} event - Change event
     * @param {Object} params - Event parameters
     * @param {string} params.value - Selected device ID
     */
    const handleMicrophoneChange = async (event, { value }) => {
        try {
            const device = microphoneDevices.find(d => d.deviceId === value);
            if (device) {
                await setActiveMicrophone(value);
                SettingsStore.setAudioInputDevice(value, device.label);
            }
        } catch (error) {
            console.error('[AudioSettings] Failed to change microphone:', error);
        }
    };

    /**
     * Handle speaker device selection change
     * 
     * Applies the selected device via LiveKit and saves the preference
     * (both deviceId and label) to localStorage for future sessions.
     * 
     * @param {Event} event - Change event
     * @param {Object} params - Event parameters
     * @param {string} params.value - Selected device ID
     */
    const handleSpeakerChange = async (event, { value }) => {
        try {
            const device = speakerDevices.find(d => d.deviceId === value);
            if (device) {
                await setActiveSpeaker(value);
                SettingsStore.setAudioOutputDevice(value, device.label);
            }
        } catch (error) {
            console.error('[AudioSettings] Failed to change speaker:', error);
        }
    };

    /**
     * Handle test sound playback
     * 
     * Plays or stops a test sound to verify speaker output
     */
    const handleTestSound = () => {
        if (isPlayingTestSound) {
            // Stop the test sound
            AppStore.stopSound('audio-check');

            // Clear auto-stop timer
            if (autoStopTimerRef.current) {
                clearTimeout(autoStopTimerRef.current);
                autoStopTimerRef.current = null;
            }

            setIsPlayingTestSound(false);
        } else {
            // Play the test sound at current volume
            const currentVolume = volume / 100; // Convert 0-100 to 0.0-1.0

            AppStore.playSound('audio-check', {
                volume: currentVolume,
                onend: () => {
                    setIsPlayingTestSound(false);
                }
            });

            setIsPlayingTestSound(true);

            // Auto-stop after 15 seconds
            autoStopTimerRef.current = setTimeout(() => {
                AppStore.stopSound('audio-check');
                setIsPlayingTestSound(false);
            }, SOUND_TEST_DURATION);
        }
    };

    /**
     * Render microphone device options for the dropdown
     * 
     * @returns {Array<JSX.Element>} SimpleSelect.Option elements for each device
     */
    const renderMicrophoneOptions = () => {
        if (!microphoneDevices || microphoneDevices.length === 0) {
            return [<SimpleSelect.Option key="none" id="none" value="">No microphones available</SimpleSelect.Option>];
        }

        return microphoneDevices.map((device) => (
            <SimpleSelect.Option
                key={device.deviceId}
                id={device.deviceId}
                value={device.deviceId}
            >
                {device.label || `Microphone ${device.deviceId.substring(0, 8)}`}
            </SimpleSelect.Option>
        ));
    };

    /**
     * Render speaker device options for the dropdown
     * 
     * @returns {Array<JSX.Element>} SimpleSelect.Option elements for each device
     */
    const renderSpeakerOptions = () => {
        if (!speakerDevices || speakerDevices.length === 0) {
            return [<SimpleSelect.Option key="none" id="none" value="">No speakers available</SimpleSelect.Option>];
        }

        return speakerDevices.map((device) => (
            <SimpleSelect.Option
                key={device.deviceId}
                id={device.deviceId}
                value={device.deviceId}
            >
                {device.label || `Speaker ${device.deviceId.substring(0, 8)}`}
            </SimpleSelect.Option>
        ));
    };

    /**
     * Get the device ID to display in the microphone dropdown
     * 
     * Matches saved device preference by label (primary) or ID (fallback).
     * Falls back to active device or first available device if no match.
     * 
     * @returns {string} Device ID to select in dropdown
     */
    const getMicrophoneValue = () => {
        // Wait for devices to load
        if (microphoneDevices.length === 0) return '';

        if (audioInputDevice) {
            // Try to match by label first
            const matchedDevice = microphoneDevices.find(d => d.label === audioInputDevice.label) ||
                microphoneDevices.find(d => d.deviceId === audioInputDevice.deviceId);
            if (matchedDevice) {
                return matchedDevice.deviceId;
            }
        }

        // Fallback to active device or first device
        if (activeMicrophoneId && activeMicrophoneId !== 'default') {
            return activeMicrophoneId;
        }

        return microphoneDevices[0]?.deviceId || '';
    };

    /**
     * Get the device ID to display in the speaker dropdown
     * 
     * Matches saved device preference by label (primary) or ID (fallback).
     * Falls back to active device or first available device if no match.
     * 
     * @returns {string} Device ID to select in dropdown
     */
    const getSpeakerValue = () => {
        // Wait for devices to load
        if (speakerDevices.length === 0) return '';

        if (audioOutputDevice) {
            // Try to match by label first
            const matchedDevice = speakerDevices.find(d => d.label === audioOutputDevice.label) ||
                speakerDevices.find(d => d.deviceId === audioOutputDevice.deviceId);
            if (matchedDevice) {
                return matchedDevice.deviceId;
            }
        }

        // Fallback to active device or first device
        if (activeSpeakerId && activeSpeakerId !== 'default') {
            return activeSpeakerId;
        }

        return speakerDevices[0]?.deviceId || '';
    };

    return (
        <View as="div" padding="x-small">
            <View as="div" padding="none none small none">
                {attachedToProctorRoomSid ? (
                    <Text>
                        Select your preferred microphone and speaker devices. Changes are applied immediately and <strong>will be applied to all sessions in this room</strong>.
                    </Text>
                ) : (
                    <Text>
                        Select your preferred microphone and speaker devices. Changes are applied immediately.
                    </Text>
                )}
            </View>

            <FormFieldGroup
                description=""
                layout="stacked"
                rowSpacing="medium"
            >
                {isMicrophoneEnabled && (
                    <Card>
                        <Card.Header
                            background="brand"
                            padding={CONSTANTS.STYLES.CARD_HEADER_PADDING}
                        >
                            <Card.Title>
                                <Text variant={CONSTANTS.STYLES.CARD_TITLE_VARIANT}>
                                    Microphone
                                </Text>
                            </Card.Title>
                        </Card.Header>
                        <Card.Body padding="small">
                            <SimpleSelect
                                renderLabel={<ScreenReaderContent>Microphone Options</ScreenReaderContent>}
                                value={getMicrophoneValue()}
                                onChange={handleMicrophoneChange}
                            >
                                {renderMicrophoneOptions()}
                            </SimpleSelect>
                        </Card.Body>
                    </Card>
                )}

                <Card>
                    <Card.Header
                        background="brand"
                        padding={CONSTANTS.STYLES.CARD_HEADER_PADDING}
                    >
                        <Card.Title>
                            <Text variant={CONSTANTS.STYLES.CARD_TITLE_VARIANT}>
                                Speaker / Headphones
                            </Text>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body padding="small">
                        <SimpleSelect
                            renderLabel={<ScreenReaderContent>Speaker / Headphones Options</ScreenReaderContent>}
                            value={getSpeakerValue()}
                            onChange={handleSpeakerChange}
                        >
                            {renderSpeakerOptions()}
                        </SimpleSelect>

                        <View as="div" margin="small none none none">
                            <Text>Volume: {volume === '0' ? 'Muted' : `${volume}%`}</Text>
                            <Flex alignItems="center" gap="small">
                                <Flex.Item shouldGrow shouldShrink>
                                    <RangeInput
                                        renderLabel="Volume"
                                        value={volume}
                                        onChange={(value) => {
                                            PlayerStore.setVolume(value);
                                            // If test sound is playing, adjust its volume in real-time (throttled)
                                            throttledVolumeUpdate(value);
                                        }}
                                        min={0}
                                        max={100}
                                        step={1}
                                        displayValue={false}
                                    />
                                </Flex.Item>
                                <Flex.Item>
                                    <Tooltip
                                        renderTip={isPlayingTestSound ? "Stop test sound" : "Play test sound"}
                                        placement="top"
                                    >
                                        <IconButton
                                            screenReaderLabel={isPlayingTestSound ? "Stop test sound" : "Play test sound"}
                                            onClick={handleTestSound}
                                            size="small"
                                        >
                                            {isPlayingTestSound ? <IconStopLine /> : <IconPlayLine />}
                                        </IconButton>
                                    </Tooltip>
                                </Flex.Item>
                            </Flex>
                            {volume === '100' &&
                                <Alert status="info" margin="small 0 0 0">
                                    <Text><b>Volume not loud enough?</b> You may need to adjust your computer's master volume or speaker volume.</Text>
                                </Alert>
                            }
                        </View>
                    </Card.Body>
                </Card>
            </FormFieldGroup>
        </View>
    );
}

export default AudioSettings;
